/**
 * Protected Route Wrapper
 * - Checks if user is authenticated
 * - Loads workspace based on slug from URL
 * - Redirects to login if not authenticated
 * - Shows error if workspace not found or no access
 */

import { useEffect, useState, ReactNode } from 'react';
import { useParams, Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../lib/auth';
import apiClient from '../lib/api';

interface ProtectedRouteProps {
  children: ReactNode;
}

export default function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { workspaceSlug } = useParams<{ workspaceSlug: string }>();
  const location = useLocation();
  const { user, workspace, setWorkspace, isAuthenticated } = useAuthStore();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadWorkspace = async () => {
      // Not logged in - redirect to login
      if (!user || !isAuthenticated) {
        setIsLoading(false);
        return;
      }

      // No workspace slug in URL - this shouldn't happen
      if (!workspaceSlug) {
        setError('No workspace specified in URL');
        setIsLoading(false);
        return;
      }

      // Already have the correct workspace loaded
      if (workspace && workspace.slug === workspaceSlug) {
        setIsLoading(false);
        return;
      }

      // Need to load/switch workspace
      try {
        console.log('[PROTECTED] Loading workspace:', workspaceSlug);

        // Get user's workspaces
        const response = await apiClient.get('/auth/workspaces');
        const workspaces = response.data.workspaces || [];

        console.log('[PROTECTED] User workspaces:', workspaces);

        // Find workspace by slug
        const targetWorkspace = workspaces.find(
          (ws: any) => ws.slug === workspaceSlug
        );

        if (!targetWorkspace) {
          console.error('[PROTECTED] Workspace not found:', workspaceSlug);
          setError(`Workspace "${workspaceSlug}" not found or you don't have access`);
          setIsLoading(false);
          return;
        }

        // Switch to this workspace if needed
        if (!targetWorkspace.isActive) {
          console.log('[PROTECTED] Switching to workspace:', targetWorkspace.name);
          const switchResponse = await apiClient.post('/auth/workspace/switch', {
            workspaceId: targetWorkspace.id,
          });

          const switchData = switchResponse.data.data || switchResponse.data;
          if (switchData.workspace) {
            setWorkspace(switchData.workspace);
          }
        } else {
          // Just set the workspace in store if not already set
          setWorkspace(targetWorkspace);
        }

        setIsLoading(false);
      } catch (err: any) {
        console.error('[PROTECTED] Failed to load workspace:', err);
        setError(
          err.response?.data?.message || 'Failed to load workspace'
        );
        setIsLoading(false);
      }
    };

    loadWorkspace();
  }, [workspaceSlug, user, isAuthenticated, workspace, setWorkspace]);

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white dark:bg-gray-900">
        <div className="flex flex-col items-center gap-4">
          <div className="w-16 h-16 border-4 border-gray-200 dark:border-gray-700 border-t-black dark:border-t-white rounded-full animate-spin"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading workspace...</p>
        </div>
      </div>
    );
  }

  // Not authenticated - redirect to login
  if (!user || !isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Workspace error
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white dark:bg-gray-900 p-4">
        <div className="max-w-md w-full">
          <div className="p-6 bg-red-50 dark:bg-red-900/20 border-2 border-red-500 rounded-lg">
            <div className="flex items-start gap-4">
              <svg
                className="w-6 h-6 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <div>
                <h3 className="font-semibold text-red-900 dark:text-red-200 mb-1">
                  Workspace Error
                </h3>
                <p className="text-sm text-red-800 dark:text-red-300 mb-4">
                  {error}
                </p>
                <a
                  href="/login"
                  className="text-sm font-medium text-red-700 dark:text-red-300 hover:underline"
                >
                  ← Back to login
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // All good - render children
  return <>{children}</>;
}
